// src/components/ProtectedRoute.jsx
import * as React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { api } from '../lib/api';
import { getAccess, clearAuth, saveUser } from '../lib/auth';

/**
 * @typedef {'vendor'|'admin'|'superadmin'} Role
 */

/**
 * @param {{ children: React.ReactNode, roles?: Role[] }} props
 */
export default function ProtectedRoute({ children, roles = [] }) {
  const loc = useLocation();
  const [checking, setChecking] = React.useState(true);
  const [allowed, setAllowed] = React.useState(false);
  const [redirectTo, setRedirectTo] = React.useState('/services');

  React.useEffect(() => {
    let cancelled = false;

    const access = getAccess();
    if (!access) {
      setChecking(false);
      setAllowed(false);
      setRedirectTo('/services');
      return;
    }

    (async () => {
      try {
        // Le backend doit refuser si désactivé / non authentifié (403 / 401)
        const { data } = await api.get('/me');

        // Enregistre l'utilisateur courant dans le storage
        saveUser(data || null);

        // Vérification des rôles
        const userRole = data && data.role;
        if (Array.isArray(roles) && roles.length > 0 && roles.indexOf(userRole) === -1) {
          setAllowed(false);
          setRedirectTo(
            userRole === 'admin' || userRole === 'superadmin'
              ? '/admin'
              : '/mes-envois'
          );
        } else {
          setAllowed(true);
        }
      } catch {
        clearAuth();
        setAllowed(false);
        setRedirectTo('/services');
      } finally {
        if (!cancelled) setChecking(false);
      }
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loc.pathname, Array.isArray(roles) ? roles.join('|') : '']);

  if (checking) return null;
  if (!allowed) return <Navigate to={redirectTo} replace state={{ from: loc }} />;

  return <>{children}</>;
}